import { useState } from 'react';
import { handoverTargets, removedLine } from './remove-question';
import type { PaigeDestination, PaigeDestinationsBridge } from './paige.global';
import { trackProduct } from './product-tracker';

/** The destination Remove is asking about, with the job's destinations as Paige answered on asking. */
export interface Removal {
  row: PaigeDestination;
  targets: PaigeDestination[];
}

/**
 * Remove on a destination's row (or in its sheet): asks Paige afresh how many documents wait for it,
 * as CapturePoint asks the engine rather than the last refresh, so a backlog just queued is in the
 * question; then removes it, sending the waiting ones to another destination when chosen, and says so
 * in the card's note line.
 */
export function useDestinationRemoval(
  bridge: PaigeDestinationsBridge | undefined,
  setNote: (note: string) => void,
  reload: () => void,
) {
  const [removal, setRemoval] = useState<Removal | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ask = async (row: PaigeDestination) => {
    setError(null);
    const rows = await bridge?.list().catch(() => null);
    const fresh = rows?.find((r) => r.id === row.id) ?? row;
    setRemoval({ row: fresh, targets: handoverTargets(rows ?? [], fresh) });
  };
  const confirm = async (sendTo?: string) => {
    if (!bridge || !removal) return;
    setBusy(true);
    setError(null);
    try {
      setNote(await removeAndSay(bridge, removal, sendTo));
      setRemoval(null);
      reload();
    } catch (failure) {
      setError(
        failure instanceof Error ? failure.message : 'Paige could not remove the destination',
      );
    } finally {
      setBusy(false);
    }
  };
  const cancel = () => setRemoval(null);
  return { removal, busy, error, ask, confirm, cancel };
}

/** Removes it (sending the waiting documents on when chosen); the card's line for what happened. */
async function removeAndSay(
  bridge: PaigeDestinationsBridge,
  { row, targets }: Removal,
  sendTo: string | undefined,
): Promise<string> {
  const result = await bridge.remove(row.id, sendTo);
  trackProduct('export.removed', { destination: row.kind });
  return removedLine(
    row,
    result,
    targets.find((t) => t.id === sendTo),
  );
}
